import { useMutation } from '@apollo/client';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { DealTroubleshootingFeedbackPayload } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DealTroubleshootingAgentRegisterFeedbackDocument } from '../../../graphql/mutation/deals/DealTroubleshootingAgentRegisterFeedback.generated.tsx';

export const useDealTroubleshootingFeedback: DealProviderConfig['useDealTroubleshootingFeedback'] = () => {
  const [error, setError] = useState<string | null>(null);
  const [registerFeedback, { loading: isSubmitting }] = useMutation(DealTroubleshootingAgentRegisterFeedbackDocument);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const submitFeedback = useCallback(
    async (payload: DealTroubleshootingFeedbackPayload) => {
      setError(null);
      try {
        const { data } = await registerFeedback({
          variables: {
            deal_id: payload.dealId,
            langfuse_trace_id: payload.langfuseTraceId,
            score: payload.score,
            comment: payload.comment?.trim() || null,
          },
        });

        const response = data?.dealTroubleshootingAgentRegisterFeedback;

        if (!response) {
          throw new Error('Failed to submit deal troubleshooting feedback: empty response.');
        }

        enqueueSnackbar(t('Thank you for submitting feedback!'), { variant: 'success' });
      } catch (err) {
        const normalizedError =
          typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message?: string }).message)
            : t('Failed to submit deal troubleshooting feedback.');
        setError(normalizedError);
        enqueueSnackbar(normalizedError, { variant: 'error' });
        throw err;
      }
    },
    [registerFeedback, enqueueSnackbar, t]
  );

  return {
    submitFeedback,
    isSubmitting,
    error,
    resetError: () => setError(null),
  };
};
